import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import BrainLogo from "@/components/BrainLogo";
import api from "@/lib/api";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) { setStatus("error"); setMessage("No token provided."); return; }
    api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(r => { setStatus("success"); setMessage(r.data.message || "Email verified!"); })
      .catch(e => { setStatus("error"); setMessage(e?.response?.data?.detail || "Invalid or expired link."); });
  }, [token]);

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-5 mesh-bg">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-[400px] text-center"
      >
        <Link to="/" className="flex items-center justify-center gap-2 mb-12">
          <BrainLogo size={32} />
          <span className="font-semibold tracking-tight text-[18px]">Studivault</span>
        </Link>

        {status === "loading" && (
          <div className="flex flex-col items-center gap-4 text-[#B0AEBC]">
            <Loader2 size={32} className="animate-spin text-[#B8A4FF]" />
            <p className="text-[15px]">Verifying your email…</p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="verify-success">
            <div className="w-16 h-16 rounded-2xl bg-green-900/20 border border-green-500/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={28} className="text-green-400" />
            </div>
            <h1 className="display-3 mb-3">Email verified!</h1>
            <p className="text-[15px] text-[#B0AEBC] mb-8">
              Your Studivault account is fully activated. Start studying!
            </p>
            <Link to="/app" className="btn btn-primary w-full block text-center py-3.5" data-testid="verify-go-app">
              Go to dashboard
            </Link>
          </div>
        )}

        {status === "error" && (
          <div data-testid="verify-error">
            <div className="w-16 h-16 rounded-2xl bg-red-900/20 border border-red-500/30 flex items-center justify-center mx-auto mb-6">
              <XCircle size={28} className="text-red-400" />
            </div>
            <h1 className="display-3 mb-3">Verification failed</h1>
            <p className="text-[15px] text-[#B0AEBC] mb-8">{message}</p>
            <Link to="/login" className="btn btn-primary w-full block text-center py-3.5">
              Back to sign in
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
